import { cn } from '@/lib/cn'

/**
 * A price, set in mono and brass.
 *
 * Mono is a deliberate choice, not decoration: the catalogue is a-la-carte, so a
 * visitor's real job on these pages is to add numbers up. Tabular monospace makes
 * a column of prices scannable and comparable, and it makes the page look like an
 * honest tariff rather than a sales sheet — which is the same thing the pricing
 * configurator says in words ("Every price here is the real price").
 *
 * Brass is the "consequence" accent: it marks what the reader gets or chooses.
 *
 * ALWAYS pass a value from a `@gjl/voice/pricing` accessor. Never a literal —
 * `docs/conventions/pricing.md` is explicit that `pricing.ts` is the only source,
 * and every drift found in the 2026-07-29 sweep was a hardcoded string.
 */
interface PriceProps {
  /** The rendered price, e.g. `$225/mo`. Must come from a pricing accessor. */
  children: React.ReactNode
  /** Larger treatment for a card's headline price. */
  size?: 'sm' | 'md' | 'lg'
  /** Quiet qualifier after the number, e.g. "one-time" or "+ 1.5% per ticket". */
  note?: React.ReactNode
  className?: string
}

const SIZES = {
  sm: 'text-sm',
  md: 'text-lg',
  lg: 'text-2xl md:text-3xl',
} as const

export function Price({ children, size = 'md', note, className }: PriceProps) {
  return (
    <span className={cn('inline-flex items-baseline gap-2 whitespace-nowrap', className)}>
      <span className={cn('text-accent font-mono font-medium tracking-tight tabular-nums', SIZES[size])}>
        {children}
      </span>
      {note && <span className="text-muted-foreground font-mono text-xs">{note}</span>}
    </span>
  )
}
